// Admin → Work Management → Workload & Health → Capacity (Workload
// Intelligence Phase 2). Per-person capacity in RELATIVE effort units per week
// (the same scale as the effort weights, XS=1 … XL=8 by default) — never
// hours. Only people listed here get a utilization percentage anywhere in the
// product; everyone else reads "Capacity not set". Rows save immediately
// (PUT / DELETE per person) — they are not part of the policy Save button;
// the "Show utilization" switch IS policy and is rendered by the page.
// People come from the company-wide directory search (the admin is composing
// configuration, not using an assignment scope).
import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { Loader2, Plus, Search, Trash2 } from 'lucide-react';
import { getApiErrorMessage } from '@/lib/apiError';
import { Button } from '@/features/shared/components/ui/button';
import { Input } from '@/features/shared/components/ui/input';
import { Label } from '@/features/shared/components/ui/label';
import { PeoplePicker, type Person } from '@/features/shared/components/PeoplePicker';
import { useDebouncedValue } from '@/features/shared/components/ListWorkbench';
import type { EligiblePage } from '@/services/workService';
import {
  CAPACITY_UNITS_MAX, type CapacityItem, deleteCapacity, putCapacity, validateCapacityUnits,
} from '@/services/workloadService';
import { useWorkloadCapacity } from '@/features/work/api/workloadHooks';
import { fetchDirectoryPage } from './WorkAssignmentGroups';

type K = (key: string, dflt: string, opts?: Record<string, unknown>) => string;
const PAGE = 25;
const unitsCls = 'w-24 tabular-nums';

export interface WorkloadCapacitySectionProps {
  /** Tests inject a fetcher; production searches the company directory. */
  fetchPage?: (q: string, page: number) => Promise<EligiblePage>;
  disabled?: boolean;
}

/** One configured person: name + subline, department, units (inline edit),
 * note (inline edit), remove. Edits PUT on Save (or Enter); nothing is sent
 * while the units are invalid. */
const CapacityRow: React.FC<{
  k: K; item: CapacityItem; disabled?: boolean;
  onSaved: (next: CapacityItem) => void; onRemoved: (employeeId: string) => void;
}> = ({ k, item, disabled, onSaved, onRemoved }) => {
  const [units, setUnits] = useState(String(item.capacity_units));
  const [note, setNote] = useState(item.note || '');
  const [busy, setBusy] = useState(false);
  useEffect(() => { setUnits(String(item.capacity_units)); setNote(item.note || ''); }, [item]);
  const parsed = validateCapacityUnits(units);
  const invalid = units.trim() !== '' && parsed === null;
  const dirty = parsed !== null && (parsed !== item.capacity_units || (note.trim() || '') !== (item.note || ''));

  const save = async () => {
    if (parsed === null) return;
    setBusy(true);
    try {
      const next = await putCapacity(item.employee_id, { capacity_units: parsed, ...(note.trim() ? { note: note.trim() } : {}) });
      onSaved({ ...item, ...next, capacity_units: next?.capacity_units ?? parsed, note: next?.note ?? (note.trim() || null) });
      toast.success(k('cap_saved', 'Capacity saved.'));
    } catch (e) {
      toast.error(getApiErrorMessage(e, k('cap_save_failed', 'Could not save capacity.')));
    } finally {
      setBusy(false);
    }
  };
  const remove = async () => {
    setBusy(true);
    try {
      await deleteCapacity(item.employee_id);
      onRemoved(item.employee_id);
      toast.success(k('cap_removed', 'Capacity removed — utilization is no longer shown for this person.'));
    } catch (e) {
      toast.error(getApiErrorMessage(e, k('cap_remove_failed', 'Could not remove capacity.')));
    } finally {
      setBusy(false);
    }
  };
  const subline = item.job_title || '';
  return (
    <tr data-testid={`capacity-row-${item.employee_id}`} className="hover:bg-muted/40">
      <td className="py-2 px-4 align-middle">
        <span className="block text-foreground truncate">{item.display_name}</span>
        {subline && <span className="block text-[11px] text-muted-foreground truncate">{subline}</span>}
      </td>
      <td className="py-2 px-2 align-middle text-muted-foreground">{item.department_name || '—'}</td>
      <td className="py-2 px-2 align-middle">
        <Input type="number" min={1} max={CAPACITY_UNITS_MAX} step={0.5} className={unitsCls} value={units} disabled={disabled || busy}
          aria-label={k('cap_units_for', 'Capacity units for {{name}}', { name: item.display_name })}
          aria-invalid={invalid || undefined}
          onChange={(e) => setUnits(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && dirty) void save(); }} />
        {invalid && <p className="text-[11px] text-danger-strong mt-0.5" role="alert">{k('cap_units_invalid', 'Enter a number above 0 and up to 1000.')}</p>}
      </td>
      <td className="py-2 px-2 align-middle">
        <Input value={note} disabled={disabled || busy} className="min-w-[140px]"
          aria-label={k('cap_note_for', 'Note for {{name}}', { name: item.display_name })}
          placeholder={k('cap_note_ph', 'Optional note (e.g. part-time)')}
          onChange={(e) => setNote(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && dirty) void save(); }} />
      </td>
      <td className="py-2 px-2 align-middle text-right whitespace-nowrap">
        <Button size="sm" variant="outline" disabled={disabled || busy || !dirty} onClick={save}
          aria-label={k('cap_save_for', 'Save capacity for {{name}}', { name: item.display_name })}>
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : k('cap_save', 'Save')}
        </Button>
        <Button size="sm" variant="ghost" disabled={disabled || busy} onClick={remove} className="ml-1 text-muted-foreground hover:text-danger-strong"
          aria-label={k('cap_remove_for', 'Remove capacity for {{name}}', { name: item.display_name })}>
          <Trash2 className="w-4 h-4" />
        </Button>
      </td>
    </tr>
  );
};

export const WorkloadCapacitySection: React.FC<WorkloadCapacitySectionProps> = ({ fetchPage, disabled }) => {
  const { t } = useTranslation('work');
  const k: K = (key, dflt, opts = {}) => t(`policy_wl_${key}`, { defaultValue: dflt, ...opts }) as string;
  const [q, setQ] = useState('');
  const qDebounced = useDebouncedValue(q, 300);
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState<CapacityItem[]>([]);
  // Local add/remove shifts the server total until the next fetch.
  const [delta, setDelta] = useState(0);
  const params = useMemo(() => ({ q: qDebounced || undefined, page, page_size: PAGE }), [qDebounced, page]);
  useEffect(() => { setPage(1); }, [qDebounced]);
  const list = useWorkloadCapacity(params, true);
  useEffect(() => {
    if (!list.data) return;
    setRows((prev) => (list.data!.page > 1 ? [...prev, ...list.data!.items] : list.data!.items));
    setDelta(0);
  }, [list.data]);
  useEffect(() => {
    if (list.error) toast.error(getApiErrorMessage(list.error, k('cap_load_failed', 'Could not load capacity settings.')));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list.error]);

  // Add person: pick from the directory, then units (+ optional note) → PUT.
  const [adding, setAdding] = useState<Person | null>(null);
  const [addUnits, setAddUnits] = useState('');
  const [addNote, setAddNote] = useState('');
  const [addBusy, setAddBusy] = useState(false);
  const addParsed = validateCapacityUnits(addUnits);
  const addInvalid = addUnits.trim() !== '' && addParsed === null;
  const search = fetchPage ?? ((query: string, pg: number) => fetchDirectoryPage(query, pg));
  const configuredIds = useMemo(() => rows.map((r) => r.employee_id), [rows]);

  const confirmAdd = async () => {
    if (!adding) return;
    if (addParsed === null) {
      toast.error(k('cap_units_invalid', 'Enter a number above 0 and up to 1000.'));
      return;
    }
    setAddBusy(true);
    try {
      const saved = await putCapacity(adding.employee_id, { capacity_units: addParsed, ...(addNote.trim() ? { note: addNote.trim() } : {}) });
      // Prefer the server's echo (it knows the department); fall back to the
      // picked person so the row appears even on a sparse response.
      const item: CapacityItem = {
        employee_id: adding.employee_id,
        display_name: saved?.display_name || adding.display_name,
        job_title: saved?.job_title ?? adding.job_title ?? null,
        department_name: saved?.department_name ?? adding.department_name ?? null,
        capacity_units: saved?.capacity_units ?? addParsed,
        note: saved?.note ?? (addNote.trim() || null),
        updated_at: saved?.updated_at || new Date().toISOString(),
      };
      setRows((prev) => {
        if (!prev.some((r) => r.employee_id === item.employee_id)) setDelta((d) => d + 1);
        return [item, ...prev.filter((r) => r.employee_id !== item.employee_id)];
      });
      setAdding(null); setAddUnits(''); setAddNote('');
      toast.success(k('cap_saved', 'Capacity saved.'));
    } catch (e) {
      toast.error(getApiErrorMessage(e, k('cap_save_failed', 'Could not save capacity.')));
    } finally {
      setAddBusy(false);
    }
  };

  const total = Math.max(rows.length, (list.data?.total ?? 0) + delta);

  return (
    <div className="space-y-3" data-testid="wl-capacity">
      <div className="flex flex-wrap items-end gap-2">
        <div className="flex-1 min-w-[220px]">
          <Label htmlFor="wl-cap-add">{k('cap_add_person', 'Add person')}</Label>
          <PeoplePicker id="wl-cap-add" value={adding} onChange={(p) => { setAdding(p); setAddUnits(''); setAddNote(''); }}
            fetchPage={search} disabled={disabled || addBusy} exclude={configuredIds} debounceMs={fetchPage ? 0 : 250}
            placeholder={k('cap_add_ph', 'Search the company directory…')}
            emptyHint={k('cap_add_hint', 'Type a name to find someone.')} />
        </div>
      </div>
      {adding && (
        <div className="flex flex-wrap items-end gap-2 rounded-[10px] border border-border bg-muted/30 p-3" data-testid="wl-capacity-add">
          <div>
            <Label htmlFor="wl-cap-add-units">{k('cap_units', 'Capacity (units per week)')}</Label>
            <Input id="wl-cap-add-units" type="number" min={1} max={CAPACITY_UNITS_MAX} step={0.5} className={unitsCls} value={addUnits}
              autoFocus disabled={addBusy} aria-invalid={addInvalid || undefined}
              onChange={(e) => setAddUnits(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') void confirmAdd(); }} />
          </div>
          <div className="flex-1 min-w-[160px]">
            <Label htmlFor="wl-cap-add-note">{k('cap_note', 'Note (optional)')}</Label>
            <Input id="wl-cap-add-note" value={addNote} disabled={addBusy} placeholder={k('cap_note_ph', 'Optional note (e.g. part-time)')}
              onChange={(e) => setAddNote(e.target.value)} />
          </div>
          <Button size="sm" disabled={addBusy || addParsed === null} onClick={confirmAdd}>
            {addBusy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Plus className="w-3.5 h-3.5 mr-1" />}
            {k('cap_add', 'Add')}
          </Button>
          <Button size="sm" variant="ghost" disabled={addBusy} onClick={() => setAdding(null)}>{t('common.cancel', { defaultValue: 'Cancel' })}</Button>
          {addInvalid && <p className="w-full text-[11px] text-danger-strong" role="alert">{k('cap_units_invalid', 'Enter a number above 0 and up to 1000.')}</p>}
        </div>
      )}

      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[180px] max-w-sm">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} className="pl-8"
            placeholder={k('cap_search_ph', 'Filter configured people…')}
            aria-label={k('cap_search', 'Filter configured people')} />
        </div>
        <span className="text-xs text-muted-foreground" data-testid="wl-capacity-count">
          {k('cap_count', '{{count}} people with capacity set', { count: total })}
        </span>
      </div>

      <div className="rounded-[10px] border border-border overflow-x-auto">
        {list.loading && rows.length === 0 ? (
          <p className="p-4 text-sm text-muted-foreground flex items-center gap-2" role="status">
            <Loader2 className="w-4 h-4 animate-spin" /> {t('common.loading', { defaultValue: 'Loading…' })}
          </p>
        ) : rows.length === 0 ? (
          <p className="p-4 text-sm text-muted-foreground" data-testid="wl-capacity-empty">
            {q ? k('cap_no_match', 'No configured people match.') : k('cap_none', 'No one has capacity set yet. Add a person to start showing utilization.')}
          </p>
        ) : (
          <table className="w-full text-sm" data-testid="wl-capacity-table">
            <thead>
              <tr className="text-xs text-muted-foreground">
                <th scope="col" className="text-left font-medium py-2 px-4">{k('cap_col_person', 'Person')}</th>
                <th scope="col" className="text-left font-medium py-2 px-2">{k('cap_col_department', 'Department')}</th>
                <th scope="col" className="text-left font-medium py-2 px-2">{k('cap_col_units', 'Capacity (units/week)')}</th>
                <th scope="col" className="text-left font-medium py-2 px-2">{k('cap_col_note', 'Note')}</th>
                <th scope="col" className="py-2 px-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {rows.map((r) => (
                <CapacityRow key={r.employee_id} k={k} item={r} disabled={disabled}
                  onSaved={(next) => setRows((prev) => prev.map((x) => (x.employee_id === next.employee_id ? next : x)))}
                  onRemoved={(id) => { setRows((prev) => prev.filter((x) => x.employee_id !== id)); setDelta((d) => d - 1); }} />
              ))}
            </tbody>
          </table>
        )}
      </div>
      {list.data?.has_more && (
        <div className="flex justify-center">
          <Button variant="outline" size="sm" disabled={list.loading} onClick={() => setPage((p) => p + 1)}>
            {t('common.load_more', { defaultValue: 'Load more' })}
          </Button>
        </div>
      )}
    </div>
  );
};

export default WorkloadCapacitySection;
